import styled from 'styled-components';
import { YearlyRevenueDto } from 'api/models';
import { Line, LineChart, CartesianGrid, Legend, Tooltip, YAxis, XAxis, ResponsiveContainer } from 'recharts';
import { formatCurrency } from 'utility/utility';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #1f2937;
`;

const LegendContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
`;

const LegendDot = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.color};
  margin-right: 8px;
`;

const LegendText = styled.span`
  font-size: 14px;
  color: #4b5563;
`;

const ChartContainer = styled.div`
  height: 320px;
`;

export const RevenueChart = (data: YearlyRevenueDto) => {
  const { t } = useTranslation();

  const chartData = Object.entries(data.monthlyRevenue || {}).map(([month, value]) => ({
    month,
    revenue: value,
    received: data.monthlyPayments?.[month] || 0,
  }));

  return (
    <Container>
      <Header>
        <Title>{t('revenue-chart:title', 'Revenue Overview')}</Title>
        <LegendContainer>
          <LegendItem>
            <LegendDot color="#3B82F6" />
            <LegendText>{t('revenue-chart:revenue', 'Revenue')}</LegendText>
          </LegendItem>
          <LegendItem>
            <LegendDot color="#10B981" />
            <LegendText>{t('revenue-chart:received', 'Received')}</LegendText>
          </LegendItem>
        </LegendContainer>
      </Header>

      <ChartContainer>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} />
            <YAxis tick={{ fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} />
            <Tooltip
              formatter={value => formatCurrency(Number(value))}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#3B82F6"
              strokeWidth={2}
              dot={{ fill: '#3B82F6', strokeWidth: 2 }}
              name={t('revenue-chart:revenue', 'Revenue')}
            />
            <Line
              type="monotone"
              dataKey="received"
              stroke="#10B981"
              strokeWidth={2}
              dot={{ fill: '#10B981', strokeWidth: 2 }}
              name={t('revenue-chart:received', 'Received')}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>
    </Container>
  );
};